import React, { FC, Fragment, useState } from 'react'
import { Modal, Button, Spinner } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { uploadAsync } from 'src/redux/actions/uploadAction'
import { useAppDispatch } from 'src/redux/storeHooks'
import { accessSessionTokenKeyName } from 'src/utils/constants'
import { downloadSampleFile } from 'src/utils/downloadSampleFile'

interface ShippingProviderUploadModalProps {
  show: boolean
  handleClose: () => void
  platformId: string
  platformName: string
  platformDisplayName: string
}

const ShippingProviderUploadModal: FC<ShippingProviderUploadModalProps> = ({
  show,
  handleClose,
  platformId,
  platformName,
  platformDisplayName,
}) => {
  const [csvFile, setCSVFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const [sampleDownloading, setSampleDownloading] = useState(false)
  const [invalidFileType, setInvalidFileType] = useState(false)
  const dispatch = useAppDispatch()
  const uploadItemLabel =
    platformName?.toUpperCase() === 'SHIPHERO' ? 'Shipping Method' : 'Shipping Providers'
  const uploadListLabel =
    platformName?.toUpperCase() === 'SHIPHERO' ? 'Shipping Method' : 'Shipping Provider'

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0]
    if (selectedFile) {
      const allowedExtensions = ['csv', 'xls', 'xlsx', 'tab']
      const fileName = selectedFile.name
      const fileExtension = fileName.split('.').pop()?.toLowerCase()

      if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
        setInvalidFileType(true)
        setLoading(false)
        event.target.value = ''
        return
      }
      setCSVFile(selectedFile)
      setInvalidFileType(false)
    }
  }

  const handleUploadClick = async () => {
    if (csvFile instanceof File) {
      setLoading(true)
      const result = await dispatch(
        uploadAsync({ id: platformId, csvFile, type: 'IMPORT_SHIPPING_PROVIDER' })
      )

      if (result.payload?.success) {
        toast.success(result.payload?.success)
        setCSVFile(null)
        handleClose()
        setLoading(false)
        return
      }

      if (result.payload?.data?.error) {
        toast.warning(result.payload?.data?.error)
        setLoading(false)
        return
      }
    } else {
      toast.warning('Please choose a file to upload')
    }
  }

  const handleModalClose = () => {
    setInvalidFileType(false)
    setCSVFile(null)
    handleClose()
  }

  return (
    <Fragment>
      <Modal show={show} onHide={handleModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>
            Upload CSV of {uploadItemLabel} from {platformDisplayName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='mb-3'>
            <span>
              Please go to your{' '}
              <small className='fs-6 fw-semibold'>{platformDisplayName}</small> Seller
              Portal &amp; download the {uploadListLabel} list from there to upload it here.
              <button
                className='btn btn-link btn-sm'
                disabled={sampleDownloading}
                onClick={() =>
                  downloadSampleFile(
                    `${platformName.toLowerCase()}_shipping_method`,
                    setSampleDownloading,
                    localStorage.getItem(accessSessionTokenKeyName),
                    platformName
                  )
                }
              >
                <i className='bi bi-cloud-arrow-down text-primary'></i> Download{' '}
                {sampleDownloading ? (
                  <Spinner animation='border' className='fs-10' size='sm' />
                ) : null}
              </button>
            </span>
          </div>
          <input
            type='file'
            className='form-control'
            accept='.csv, .xls, .xlsx, .tab'
            onChange={handleFileChange}
          />
          <small
            className={`form-text ${invalidFileType ? 'text-danger' : 'text-muted'}`}
          >
            Please upload a file in .csv, .xls, .xlsx or .tab format.
          </small>
        </Modal.Body>
        <Modal.Footer>
          <Button disabled={loading} variant='secondary' onClick={handleModalClose}>
            Close
          </Button>
          <Button
            disabled={loading || csvFile == null}
            variant='primary'
            onClick={handleUploadClick}
          >
            {loading ? 'Uploading...' : 'Upload'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Fragment>
  )
}

export default ShippingProviderUploadModal
